import { MutableRefObject } from 'react';
import { ITransCatTypes, Transition } from '@r3f/transitions';
import { ITuple3 } from '@r3f-component-data-structure';

/**
 * Live-preview transition rules — see PROJECT_JUTE_PREVIEW.md in the Designer repo.
 *
 * A transition's configuration effect used to serve two masters: it recomputed the from/to/duration/delay
 * AND it pulled the trigger. That was harmless while content could not change after boot, because the only
 * thing that made the effect re-run was a scene navigation. Under live preview it re-runs on every edit,
 * which produced both halves of the wrong behaviour at once — an unrelated edit replayed the whole scene
 * enter transition, while an edit that landed mid-flight froze the transition it hit, because `set()`
 * stops a running transition and nothing restarted it.
 *
 * Configuration and triggering are therefore separated, and the trigger is decided from two facts:
 *
 * | edit lands…                    | transition running | not running          |
 * |--------------------------------|--------------------|----------------------|
 * | changing values it derives from| restart            | store values only    |
 * | changing nothing it uses       | leave alone        | leave alone          |
 *
 * A navigation (this scene entering or exiting) always triggers, exactly as before.
 */
export interface ITransitionSyncState {
	/** The transition category the stored configuration was computed for. */
	category?: ITransCatTypes;
	/** Everything that configuration was derived from, flattened. */
	signature?: string;
}

export interface ITransitionSyncResult {
	/** Write the new configuration to the transition. False means nothing it uses actually changed. */
	apply: boolean;
	/** This run is a scene navigation, not a content edit — the caller's normal trigger applies. */
	navigated: boolean;
	/** A content edit landed on a running transition: restart it with the values just applied. */
	restart: boolean;
}

/**
 * Decide what a freshly computed transition configuration should do, and record it as applied.
 *
 * Call this AFTER computing from/to/duration/delay and BEFORE `set()`, since `set()` stops a running
 * transition and would make `isRunning` useless. Bail out on `apply === false`.
 *
 * `stateRef` holds the last configuration actually written, not the last one seen — an effect run that
 * bailed early (scene hidden, entity missing) leaves it alone, so the next run that does reach a category
 * still reads as a navigation.
 */
export const syncTransitionConfig = <T extends number | ITuple3>(
	stateRef: MutableRefObject<ITransitionSyncState>,
	transition: Transition<T>,
	category: ITransCatTypes,
	signature: string
): ITransitionSyncResult => {
	const navigated = stateRef.current.category !== category;
	const configChanged = stateRef.current.signature !== signature;
	if (!navigated && !configChanged) return { apply: false, navigated: false, restart: false };

	const wasRunning = transition.isRunning;
	stateRef.current = { category, signature };
	return { apply: true, navigated, restart: !navigated && wasRunning };
};

/** Flatten the content a transition derives from into a comparable key. */
export const transitionSignature = (...parts: unknown[]): string => JSON.stringify(parts);
